import React, { useEffect, useMemo, useState } from "react";
import DoctorHeader from "./DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import DoctorFooter from "./DoctorFooter";
import { IconButton, Button } from "@mui/material";
import {
  Menu,
  RefreshCw,
  Search,
  ShieldCheck,
  Clock3,
  Store,
  Mail,
  MapPin,
  ArrowRight,
  Plus,
  Edit3,
  X,
  Phone,
  MessageCircle,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SimpleSnackbar from "../Snackbar";

const emptyPharmacyForm = {
  name: "",
  licenseNumber: "",
  cacNumber: "",
  email: "",
  phoneNumber: "",
  whatsappNumber: "",
  location: {
    address: "",
    lat: "",
    lng: "",
  },
  branches: [],
};

const toPharmacyForm = (pharmacy = {}) => ({
  ...emptyPharmacyForm,
  ...pharmacy,
  location: {
    ...emptyPharmacyForm.location,
    ...(pharmacy.location || {}),
  },
  branches: Array.isArray(pharmacy.branches) ? pharmacy.branches : [],
});

const PharmaciesPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const [search, setSearch] = useState("");
  const [pharmacyModalOpen, setPharmacyModalOpen] = useState(false);
  const [pharmacyForm, setPharmacyForm] = useState(emptyPharmacyForm);
  const [editingPharmacy, setEditingPharmacy] = useState(null);
  const [savingPharmacy, setSavingPharmacy] = useState(false);

  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [snackLoading, setSnackLoading] = useState(false);

  const navigate = useNavigate();

  const fetchPharmacies = async () => {
    try {
      setSnackMessage("Loading pharmacies...");
      setSnackColor("info");
      setSnackLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/pharmacies`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setPharmacies(res.data.pharmacies || []);
      setSnackMessage("Pharmacies loaded");
      setSnackColor("success");
    } catch (err) {
      console.error(err);
      setSnackMessage("Failed to load pharmacies");
      setSnackColor("error");
    } finally {
      setSnackLoading(false);
    }
  };

  useEffect(() => {
    fetchPharmacies();
  }, []);

  const openCreatePharmacy = () => {
    setEditingPharmacy(null);
    setPharmacyForm(emptyPharmacyForm);
    setPharmacyModalOpen(true);
  };

  const openEditPharmacy = (pharmacy) => {
    setEditingPharmacy(pharmacy);
    setPharmacyForm(toPharmacyForm(pharmacy));
    setPharmacyModalOpen(true);
  };

  const savePharmacy = async () => {
    if (
      !pharmacyForm.name ||
      !pharmacyForm.email ||
      !pharmacyForm.phoneNumber ||
      !pharmacyForm.licenseNumber ||
      !pharmacyForm.cacNumber
    ) {
      setSnackMessage("Name, email, phone, license and CAC number are required");
      setSnackColor("error");
      return;
    }

    try {
      setSavingPharmacy(true);

      const payload = {
        ...pharmacyForm,
        branches: (pharmacyForm.branches || []).filter(
          (branch) => branch.name || branch.address || branch.phoneNumber
        ),
        location: {
          ...pharmacyForm.location,
          lat: pharmacyForm.location?.lat
            ? Number(pharmacyForm.location.lat)
            : undefined,
          lng: pharmacyForm.location?.lng
            ? Number(pharmacyForm.location.lng)
            : undefined,
        },
      };

      if (editingPharmacy?._id) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/pharmacy/${editingPharmacy._id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/pharmacy/register`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      setSnackMessage(editingPharmacy ? "Pharmacy updated" : "Pharmacy added");
      setSnackColor("success");
      setPharmacyModalOpen(false);
      fetchPharmacies();
    } catch (err) {
      console.error(err);
      setSnackMessage(
        err.response?.data?.message || "Could not save pharmacy"
      );
      setSnackColor("error");
    } finally {
      setSavingPharmacy(false);
    }
  };

  const verifyPharmacy = async (pharmId) => {
    try {
      setSnackMessage("Verifying pharmacy...");
      setSnackColor("info");
      setSnackLoading(true);

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/pharmacy/${pharmId}/verify`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setPharmacies((prev) =>
        prev.map((pharmacy) =>
          pharmacy._id === pharmId ? { ...pharmacy, verified: true } : pharmacy
        )
      );

      setSnackMessage("Pharmacy verified");
      setSnackColor("success");
    } catch (err) {
      console.error(err);
      setSnackMessage("Verification failed");
      setSnackColor("error");
    } finally {
      setSnackLoading(false);
    }
  };

  const filteredPharmacies = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) return pharmacies;

    return pharmacies.filter((pharmacy) => {
      const name = pharmacy.name || "";
      const email = pharmacy.email || "";
      const address = pharmacy.location?.address || "";

      return (
        name.toLowerCase().includes(value) ||
        email.toLowerCase().includes(value) ||
        address.toLowerCase().includes(value)
      );
    });
  }, [pharmacies, search]);

  const stats = useMemo(() => {
    const total = pharmacies.length;
    const verified = pharmacies.filter((p) => p.verified).length;
    const pending = total - verified;

    return {
      total,
      verified,
      pending,
    };
  }, [pharmacies]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      {/* Mobile Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Dashboard</p>
          <h1 className="text-base font-semibold text-slate-950">
            Pharmacies
          </h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Pharmacy" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Pharmacy"
          setActiveTab={() => setDrawerOpen(false)}
        />

        {/* Main */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Page Title */}
            <div className="mb-8 flex flex-col gap-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                    Pharmacies
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    Manage pharmacy partners and verification status.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={fetchPharmacies}
                  className="inline-flex h-11 w-fit items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
                >
                  <RefreshCw size={16} />
                  Refresh
                </button>

                <button
                  type="button"
                  onClick={openCreatePharmacy}
                  className="inline-flex h-11 w-fit items-center gap-2 rounded-xl bg-[#075E54] px-4 text-sm font-medium text-white transition hover:bg-[#064b43]"
                >
                  <Plus size={16} />
                  Add pharmacy
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Total pharmacies</p>
                      <p className="mt-2 text-2xl font-semibold text-slate-950">
                        {stats.total.toLocaleString()}
                      </p>
                    </div>

                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                      <Store size={18} />
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Verified</p>
                      <p className="mt-2 text-2xl font-semibold text-slate-950">
                        {stats.verified.toLocaleString()}
                      </p>
                    </div>

                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                      <ShieldCheck size={18} />
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Pending</p>
                      <p className="mt-2 text-2xl font-semibold text-slate-950">
                        {stats.pending.toLocaleString()}
                      </p>
                    </div>

                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                      <Clock3 size={18} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search */}
            <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
              <label className="mb-2 block text-xs font-medium text-slate-500">
                Search pharmacies
              </label>

              <div className="flex h-11 max-w-xl items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                <Search size={16} className="text-slate-400" />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email, or address"
                  className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
                />
              </div>
            </section>

            {/* List Header */}
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-950">
                Pharmacy list
              </h3>

              <p className="text-sm text-slate-500">
                Showing {filteredPharmacies.length.toLocaleString()} of{" "}
                {pharmacies.length.toLocaleString()}
              </p>
            </div>

            {/* Empty */}
            {filteredPharmacies.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <Store size={20} className="text-slate-500" />
                </div>

                <h3 className="text-base font-semibold text-slate-950">
                  No pharmacies found
                </h3>

                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Try refreshing or changing your search term.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {filteredPharmacies.map((pharmacy) => (
                  <PharmacyCard
                    key={pharmacy._id}
                    pharmacy={pharmacy}
                    verifyPharmacy={verifyPharmacy}
                    onEdit={() => openEditPharmacy(pharmacy)}
                    onOpen={() =>
                      navigate(`/dashboard/pharmacy/${pharmacy._id}`)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>


      {pharmacyModalOpen && (
        <PharmacyModal
          form={pharmacyForm}
          setForm={setPharmacyForm}
          editing={Boolean(editingPharmacy)}
          saving={savingPharmacy}
          onSave={savePharmacy}
          onClose={() => setPharmacyModalOpen(false)}
        />
      )}

      <SimpleSnackbar
        message={snackMessage}
        color={snackColor}
        isloading={snackLoading}
        onClose={() => setSnackMessage("")}
      />
    </div>
  );
};

const PharmacyCard = ({ pharmacy, verifyPharmacy, onOpen, onEdit }) => {
  const stats = pharmacy.prescriptionStats || {};

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-slate-300 hover:shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
              {(pharmacy.name || "P").slice(0, 2).toUpperCase()}
            </div>

            <div className="min-w-0 flex-1">
              <h4 className="truncate text-sm font-semibold text-slate-950">
                {pharmacy.name || "Unnamed pharmacy"}
              </h4>

              <p className="mt-1 flex items-center gap-1 truncate text-xs text-slate-500">
                <Mail size={13} />
                <span className="truncate">
                  {pharmacy.email || "No email"}
                </span>
              </p>

              <p className="mt-1 flex items-start gap-1 text-xs leading-5 text-slate-400">
                <MapPin size={13} className="mt-0.5 shrink-0" />
                <span className="line-clamp-2">
                  {pharmacy.location?.address || "No address"}
                </span>
              </p>

              <div className="mt-3 grid grid-cols-4 gap-1 text-center">
                <MiniStat label="Uncosted" value={stats.uncosted || 0} />
                <MiniStat label="Costed" value={stats.costed || 0} />
                <MiniStat label="Paid" value={stats.paid || 0} />
                <MiniStat label="Delivered" value={stats.delivered || 0} />
              </div>
            </div>
          </div>
        </button>

        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${
            pharmacy.verified
              ? "bg-emerald-50 text-emerald-700"
              : "bg-amber-50 text-amber-700"
          }`}
        >
          {pharmacy.verified ? "Verified" : "Pending"}
        </span>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-2">
        {!pharmacy.verified && (
          <Button
            size="small"
            onClick={() => verifyPharmacy(pharmacy._id)}
            startIcon={<ShieldCheck size={15} />}
            sx={{
              minHeight: 42,
              bgcolor: "#075E54",
              color: "white",
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#064b43",
                boxShadow: "none",
              },
            }}
          >
            Verify pharmacy
          </Button>
        )}

        <Button
          size="small"
          onClick={onOpen}
          endIcon={<ArrowRight size={15} />}
          sx={{
            minHeight: 42,
            border: "1px solid #e2e8f0",
            color: "#334155",
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              borderColor: "#cbd5e1",
              bgcolor: "#f8fafc",
              boxShadow: "none",
            },
          }}
        >
          Open pharmacy
        </Button>

        <Button
          size="small"
          onClick={onEdit}
          startIcon={<Edit3 size={15} />}
          sx={{
            minHeight: 42,
            border: "1px solid #e2e8f0",
            color: "#334155",
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              borderColor: "#cbd5e1",
              bgcolor: "#f8fafc",
              boxShadow: "none",
            },
          }}
        >
          View / edit
        </Button>
      </div>
    </article>
  );
};

const MiniStat = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 px-1.5 py-2">
    <p className="text-[10px] font-medium text-slate-400">{label}</p>
    <p className="mt-1 text-xs font-semibold text-slate-800">{value}</p>
  </div>
);

const PharmacyModal = ({
  form,
  setForm,
  editing,
  saving,
  onSave,
  onClose,
}) => {
  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateLocation = (field, value) => {
    setForm((prev) => ({
      ...prev,
      location: {
        ...(prev.location || {}),
        [field]: value,
      },
    }));
  };

  const updateBranch = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      branches: (prev.branches || []).map((branch, branchIndex) =>
        branchIndex === index ? { ...branch, [field]: value } : branch
      ),
    }));
  };

  const addBranch = () => {
    setForm((prev) => ({
      ...prev,
      branches: [
        ...(prev.branches || []),
        {
          name: "",
          address: "",
          phoneNumber: "",
          whatsappNumber: "",
          email: "",
        },
      ],
    }));
  };

  const removeBranch = (index) => {
    setForm((prev) => ({
      ...prev,
      branches: (prev.branches || []).filter(
        (_, branchIndex) => branchIndex !== index
      ),
    }));
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end bg-slate-950/50 p-3 sm:items-center sm:justify-center sm:p-6">
      <section className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              {editing ? "View / edit pharmacy" : "Add pharmacy"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Add contact details, location and branches used for costing.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[calc(92vh-145px)] overflow-y-auto p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <ModalInput
              label="Pharmacy name"
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
            />
            <ModalInput
              label="Email"
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              icon={<Mail size={15} />}
            />
            <ModalInput
              label="Phone number"
              value={form.phoneNumber}
              onChange={(event) =>
                updateField("phoneNumber", event.target.value)
              }
              icon={<Phone size={15} />}
            />
            <ModalInput
              label="WhatsApp number"
              value={form.whatsappNumber}
              onChange={(event) =>
                updateField("whatsappNumber", event.target.value)
              }
              icon={<MessageCircle size={15} />}
            />
            <ModalInput
              label="License number"
              value={form.licenseNumber}
              onChange={(event) =>
                updateField("licenseNumber", event.target.value)
              }
            />
            <ModalInput
              label="CAC number"
              value={form.cacNumber}
              onChange={(event) => updateField("cacNumber", event.target.value)}
            />
            <div className="md:col-span-2">
              <ModalInput
                label="Main address"
                value={form.location?.address || ""}
                onChange={(event) =>
                  updateLocation("address", event.target.value)
                }
                icon={<MapPin size={15} />}
              />
            </div>
            <ModalInput
              label="Latitude"
              value={form.location?.lat || ""}
              onChange={(event) => updateLocation("lat", event.target.value)}
            />
            <ModalInput
              label="Longitude"
              value={form.location?.lng || ""}
              onChange={(event) => updateLocation("lng", event.target.value)}
            />
          </div>

          <div className="mt-6">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-950">
                  Branches
                </h3>
                <p className="text-xs text-slate-500">
                  Add other pickup or delivery branch contacts.
                </p>
              </div>
              <button
                type="button"
                onClick={addBranch}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Plus size={14} />
                Add branch
              </button>
            </div>

            <div className="space-y-3">
              {(form.branches || []).map((branch, index) => (
                <div
                  key={branch._id || index}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-3"
                >
                  <div className="grid gap-3 md:grid-cols-2">
                    <ModalInput
                      label="Branch name"
                      value={branch.name || ""}
                      onChange={(event) =>
                        updateBranch(index, "name", event.target.value)
                      }
                    />
                    <ModalInput
                      label="Branch email"
                      value={branch.email || ""}
                      onChange={(event) =>
                        updateBranch(index, "email", event.target.value)
                      }
                    />
                    <ModalInput
                      label="Branch phone"
                      value={branch.phoneNumber || ""}
                      onChange={(event) =>
                        updateBranch(index, "phoneNumber", event.target.value)
                      }
                    />
                    <ModalInput
                      label="Branch WhatsApp"
                      value={branch.whatsappNumber || ""}
                      onChange={(event) =>
                        updateBranch(index, "whatsappNumber", event.target.value)
                      }
                    />
                    <div className="md:col-span-2">
                      <ModalInput
                        label="Branch address"
                        value={branch.address || ""}
                        onChange={(event) =>
                          updateBranch(index, "address", event.target.value)
                        }
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeBranch(index)}
                    className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-100"
                  >
                    Remove branch
                  </button>
                </div>
              ))}

              {!(form.branches || []).length && (
                <div className="rounded-2xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">
                  No branches added.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-[#075E54] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving && <RefreshCw size={15} className="animate-spin" />}
            Save pharmacy
          </button>
        </div>
      </section>
    </div>
  );
};

const ModalInput = ({ label, icon, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-slate-500">
      {label}
    </span>
    <span className="flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 focus-within:border-[#075E54] focus-within:ring-4 focus-within:ring-[#075E54]/10">
      {icon && <span className="text-slate-400">{icon}</span>}
      <input
        {...props}
        className="w-full bg-transparent py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400"
      />
    </span>
  </label>
);

export default PharmaciesPage;
